import type { ReactNode } from "react";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import {
  Building2,
  CreditCard,
  Download,
  FileText,
  Globe,
  Hash,
  Landmark,
  ListOrdered,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  ReceiptText,
  User,
  Wallet,
} from "lucide-react";
import {
  formatDonationDate,
  formatPaymentStatusLabel,
  paymentStatusBadgeClass,
} from "@project/types/donation-detail";
import {
  OFFLINE_DONATION_TRUSTS,
  OFFLINE_PAYMENT_MODE_OPTIONS,
  getOfflineDonationPaymentDetails,
  type OfflineDonation,
} from "@project/types/offline-donation";

type OfflineDonationDetailDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  donation: OfflineDonation | null;
  onDownloadReceipt: (donation: OfflineDonation) => void;
};

const OFFLINE_DETAIL_KEYS = new Set([
  "bank_name",
  "branch",
  "bank_transfer_id",
]);

const display = (value?: string | null) => value?.trim() || "—";

const formatAmount = (value?: string | number) =>
  `₹${parseFloat(String(value ?? "0")).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const humanizeKey = (key: string) =>
  key
    .split("_")
    .map(part =>
      part === "id" || part === "upi" || part === "vpa"
        ? part.toUpperCase()
        : part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join(" ");

const paymentModeLabel = (mode?: string) =>
  OFFLINE_PAYMENT_MODE_OPTIONS.find(option => option.value === mode)?.label ??
  (mode ? mode.toUpperCase() : "—");

export function OfflineDonationDetailDialog({
  open,
  onOpenChange,
  donation,
  onDownloadReceipt,
}: OfflineDonationDetailDialogProps) {
  if (!donation) {
    return null;
  }

  const isOnline = donation.mode === "online";
  const status = donation.status ?? donation.payments?.[0]?.status ?? "success";
  const payment = donation.payments?.[0];
  const paymentDetails = getOfflineDonationPaymentDetails(donation);
  const trust = donation.type ? OFFLINE_DONATION_TRUSTS[donation.type] : null;
  const particulars = donation.particulars ?? [];
  const particularsTotal = particulars.reduce(
    (sum, item) => sum + (parseFloat(item.amount) || 0),
    0
  );
  const onlineDetailEntries = Object.entries(paymentDetails).filter(
    ([key, value]) =>
      !OFFLINE_DETAIL_KEYS.has(key) &&
      value !== null &&
      value !== undefined &&
      String(value).trim() !== ""
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-3xl flex-col gap-0 overflow-hidden rounded-2xl p-0">
        <DialogHeader className="shrink-0 border-b px-6 pb-4 pr-14 pt-5 text-left">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <DialogTitle className="flex items-center gap-3 text-lg font-semibold">
              <span className="bg-primary/10 flex h-9 w-9 shrink-0 items-center justify-center rounded-md">
                <ReceiptText className="h-5 w-5 text-primary" />
              </span>
              Donation Details
            </DialogTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                className={
                  isOnline
                    ? "border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-50"
                    : "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-50"
                }
              >
                {isOnline ? "Online" : "Offline"}
              </Badge>
              <Badge
                className={`capitalize ${paymentStatusBadgeClass(status)}`}
              >
                {formatPaymentStatusLabel(status)}
              </Badge>
            </div>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto bg-white">
          <div className="bg-primary/5 border-b px-6 py-5">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0 space-y-1 sm:flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
                  Donation amount
                </p>
                <p className="text-2xl font-semibold sm:text-3xl">
                  {formatAmount(donation.amount)}
                </p>
              </div>
              <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
                <SummaryItem
                  label="Receipt no"
                  value={display(donation.receipt_no)}
                />
                <SummaryItem
                  label="Date"
                  value={formatDonationDate(
                    donation.created_at ?? donation.updated_at
                  )}
                />
                {trust && <SummaryItem label="Trust" value={trust.title} />}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 px-6 py-5 md:grid-cols-2">
            <InfoCard
              title="Donor information"
              icon={<User className="h-4 w-4" />}
            >
              <DetailItem
                icon={<User className="h-3.5 w-3.5" />}
                label="Donor name"
                value={display(donation.donor_name)}
              />
              {isOnline && (
                <DetailItem
                  icon={<User className="h-3.5 w-3.5" />}
                  label="Display name"
                  value={display(donation.display_name)}
                />
              )}
              <DetailItem
                icon={<Phone className="h-3.5 w-3.5" />}
                label="Phone number"
                value={display(donation.mobile_number)}
              />
              <DetailItem
                icon={<Mail className="h-3.5 w-3.5" />}
                label="Email address"
                value={display(donation.email)}
              />
              <DetailItem
                icon={<FileText className="h-3.5 w-3.5" />}
                label="PAN number"
                value={display(donation.pan_number)}
              />
            </InfoCard>

            <InfoCard title="Address" icon={<MapPin className="h-4 w-4" />}>
              <DetailItem
                icon={<MapPin className="h-3.5 w-3.5" />}
                label="Full address"
                value={display(donation.full_address)}
              />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailItem
                  icon={<Building2 className="h-3.5 w-3.5" />}
                  label="City"
                  value={display(donation.city)}
                />
                <DetailItem
                  icon={<MapPin className="h-3.5 w-3.5" />}
                  label="State"
                  value={display(donation.state)}
                />
                <DetailItem
                  icon={<Hash className="h-3.5 w-3.5" />}
                  label="Pincode"
                  value={display(donation.pincode)}
                />
                <DetailItem
                  icon={<Globe className="h-3.5 w-3.5" />}
                  label="Country"
                  value={display(donation.country)}
                />
              </div>
            </InfoCard>

            <div className="md:col-span-2">
              {isOnline ? (
                <InfoCard
                  title="Online payment"
                  icon={<CreditCard className="h-4 w-4" />}
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <DetailItem
                      icon={<Wallet className="h-3.5 w-3.5" />}
                      label="Payment method"
                      value={paymentModeLabel(
                        payment?.payment_method ?? donation.payment_mode
                      )}
                    />
                    <DetailItem
                      icon={<Hash className="h-3.5 w-3.5" />}
                      label="Razorpay order ID"
                      value={display(payment?.razorpay_order_id)}
                    />
                    <DetailItem
                      icon={<Hash className="h-3.5 w-3.5" />}
                      label="Razorpay payment ID"
                      value={display(payment?.razorpay_payment_id)}
                    />
                    <DetailItem
                      icon={<FileText className="h-3.5 w-3.5" />}
                      label="Currency"
                      value={display(payment?.currency)}
                    />
                    <DetailItem
                      icon={<FileText className="h-3.5 w-3.5" />}
                      label="Paid on"
                      value={formatDonationDate(payment?.created_at)}
                    />
                    {payment?.payment_reference?.trim() && (
                      <DetailItem
                        icon={<FileText className="h-3.5 w-3.5" />}
                        label="Payment reference"
                        value={payment.payment_reference}
                      />
                    )}
                    {onlineDetailEntries.map(([key, value]) => (
                      <DetailItem
                        key={key}
                        icon={<FileText className="h-3.5 w-3.5" />}
                        label={humanizeKey(key)}
                        value={String(value)}
                      />
                    ))}
                    <DetailItem
                      icon={<FileText className="h-3.5 w-3.5" />}
                      label="Contact consent"
                      value={donation.contact_consent ? "Yes" : "No"}
                    />
                    <DetailItem
                      icon={<FileText className="h-3.5 w-3.5" />}
                      label="Anonymous"
                      value={donation.is_anonymous ? "Yes" : "No"}
                    />
                    {donation.occasion?.trim() && (
                      <DetailItem
                        icon={<FileText className="h-3.5 w-3.5" />}
                        label="Occasion"
                        value={donation.occasion}
                      />
                    )}
                  </div>
                </InfoCard>
              ) : (
                <InfoCard
                  title="Payment details"
                  icon={<Landmark className="h-4 w-4" />}
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <DetailItem
                      icon={<FileText className="h-3.5 w-3.5" />}
                      label="Category"
                      value={display(donation.category)}
                    />
                    <DetailItem
                      icon={<FileText className="h-3.5 w-3.5" />}
                      label="Sub-category"
                      value={display(donation.sub_category)}
                    />
                    <DetailItem
                      icon={<Wallet className="h-3.5 w-3.5" />}
                      label="Payment mode"
                      value={paymentModeLabel(
                        donation.payment_mode ?? payment?.payment_method
                      )}
                    />
                    {donation.payment_mode !== "cash" && (
                      <>
                        <DetailItem
                          icon={<Landmark className="h-3.5 w-3.5" />}
                          label="Bank name"
                          value={display(paymentDetails.bank_name)}
                        />
                        <DetailItem
                          icon={<Building2 className="h-3.5 w-3.5" />}
                          label="Branch"
                          value={display(paymentDetails.branch)}
                        />
                        <DetailItem
                          icon={<Hash className="h-3.5 w-3.5" />}
                          label={
                            donation.payment_mode === "cheque"
                              ? "Cheque no."
                              : "Transaction ID / UTR"
                          }
                          value={display(paymentDetails.bank_transfer_id)}
                        />
                      </>
                    )}
                  </div>
                </InfoCard>
              )}
            </div>

            {!isOnline && (
              <div className="md:col-span-2">
                <InfoCard
                  title="Particulars"
                  icon={<ListOrdered className="h-4 w-4" />}
                >
                  {particulars.length > 0 ? (
                    <div className="overflow-hidden rounded-xl border border-border">
                      <div className="bg-muted/40 grid grid-cols-[2.5rem_1fr_9rem] gap-2 border-b border-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        <span>#</span>
                        <span>Particular</span>
                        <span className="text-right">Amount (₹)</span>
                      </div>
                      <div className="divide-y divide-border">
                        {particulars.map((item, index) => (
                          <div
                            key={`${item.name}-${item.amount}`}
                            className="grid grid-cols-[2.5rem_1fr_9rem] gap-2 px-3 py-2.5 text-sm"
                          >
                            <span className="text-muted-foreground">
                              {index + 1}
                            </span>
                            <span className="font-medium">{item.name}</span>
                            <span className="text-right">
                              {formatAmount(item.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="bg-muted/40 grid grid-cols-[2.5rem_1fr_9rem] gap-2 border-t border-border px-3 py-2.5 text-sm font-semibold">
                        <span />
                        <span className="text-right uppercase tracking-wide">
                          Total
                        </span>
                        <span className="text-right text-primary">
                          {formatAmount(particularsTotal)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No particulars recorded.
                    </p>
                  )}
                </InfoCard>
              </div>
            )}

            {isOnline && donation.special_instructions?.trim() && (
              <div className="md:col-span-2">
                <InfoCard
                  title="Special instructions"
                  icon={<MessageSquare className="h-4 w-4" />}
                >
                  <p className="border-l-[3px] border-primary pl-4 text-sm font-medium leading-relaxed text-muted-foreground">
                    {donation.special_instructions}
                  </p>
                </InfoCard>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="shrink-0 gap-3 border-t px-6 py-4 sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
          <Button
            type="button"
            className="gap-2"
            onClick={() => onDownloadReceipt(donation)}
          >
            <Download className="h-4 w-4" />
            Download receipt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="truncate text-sm font-semibold" title={value}>
        {value}
      </p>
    </div>
  );
}

function InfoCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm">
      <h3 className="mb-4 flex items-center gap-2.5 text-base font-bold">
        <span className="bg-primary/10 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-primary">
          {icon}
        </span>
        {title}
      </h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function DetailItem({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        <p className="break-words text-sm font-medium" title={value}>
          {value}
        </p>
      </div>
    </div>
  );
}
